import React from 'react';

/**
 * StepIndicator Component
 * 
 * Individual step indicator with circle, icon display, and status styling
 * Used in status timelines to show progress through different steps
 * 
 * @param {Object} step - Step object containing key, label, and icon
 * @param {number} index - Current step index
 * @param {number} currentIndex - Index of the current active step
 * @param {boolean} isCompleted - Whether this step is completed
 * @param {boolean} isCurrent - Whether this step is the current step
 * @param {boolean} isDarkMode - Dark mode state
 * @returns {JSX.Element} Step indicator component
 */
const StepIndicator = ({ 
  step, 
  index, 
  currentIndex, 
  isCompleted, 
  isCurrent, 
  isDarkMode = false 
}) => {
  // Determine the styling based on step status
  const getStepStyles = () => {
    if (isCompleted) {
      return 'bg-red-500'; // Completed steps are red
    } else if (isCurrent) {
      return 'bg-green-500'; // Current step is green
    } else {
      return 'bg-gray-200'; // Future steps are gray
    }
  };

  // Determine the icon to display
  const renderIcon = () => {
    if (isCompleted) {
      // Show check mark for completed steps
      return (
        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
          <path 
            d="M5 13l4 4L19 7" 
            stroke="#fff" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      );
    } else {
      // Show original step icon for current and future steps
      return step.icon;
    }
  };

  return (
    <div className="flex flex-col items-center">
      {/* Step Circle */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getStepStyles()}`}>
        {renderIcon()}
      </div>
      
      {/* Step Label */}
      <span className="text-xs mt-1 text-gray-500">
        {step.label}
      </span>
    </div>
  );
};

export default StepIndicator;
